import { Dropdown } from "common/commonDropdown";
import Modal from "common/Modal";
import { FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import apiCall from "redux/services/apiCall";
import { CREATE_SUPPLIER } from "redux/services/apiTypes";
import { getErrorMessage } from "utils";
import { TIME_ARRAY } from "utils/constant";
import "./style.css";
import Button from "common/commonButton";

const ManageAvailabilityModal: FC<{
  visibility: boolean;
  mode: "add" | "edit";
  editAvailabilityData?: {
    id?: number;
    day?: string;
    start?: string;
    end?: string;
  };
  staffId: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({
  visibility,
  editAvailabilityData,
  staffId,
  onClose,
  onSuccess,
  mode,
}) => {
  // Hooks
  const { t } = useTranslation("staffDetails");

  // Local States
  const [loadings, setLoadings] = useState<{
    handleFormSubmission: boolean;
    getWeekDaysList: boolean;
    handleDeleteAvailability: boolean;
  }>({
    handleFormSubmission: false,
    getWeekDaysList: false,
    handleDeleteAvailability: false,
  });
  const [formData, setFormData] = useState<{
    day: string;
    start: string;
    end: string;
  }>({
    day: "",
    start: "",
    end: "",
  });
  const [weekDaysList, setWeekDaysList] = useState<
    {
      key: string;
      display: string;
    }[]
  >([]);

  // Variables
  // In edit mode, disable save if formData is unchanged
  const isFormUnchanged =
    mode === "edit" &&
    editAvailabilityData &&
    formData.day === (editAvailabilityData.day || "") &&
    formData.start === (editAvailabilityData.start || "") &&
    formData.end === (editAvailabilityData.end || "");

  const canSubmitForm =
    formData.day &&
    formData.start &&
    formData.end &&
    !(mode === "edit" && isFormUnchanged);

  // Functions
  const getWeekDaysList = useCallback(async () => {
    setLoadings((prev) => ({ ...prev, getWeekDaysList: true }));

    try {
      const response = await apiCall({
        type: `${CREATE_SUPPLIER}list-of-days/`,
        apiType: "GET",
      });
      setWeekDaysList(response);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadings((prev) => ({ ...prev, getWeekDaysList: false }));
    }
  }, []);

  const handleFormSubmission = () => {
    setLoadings((prev) => ({ ...prev, handleFormSubmission: true }));

    let payload: { day?: string; start?: string; end?: string } = {};
    if (mode === "add") {
      payload = {
        day: formData.day,
        start: formData.start,
        end: formData.end,
      };
    } else if (mode === "edit" && editAvailabilityData) {
      if (formData.day !== (editAvailabilityData.day || ""))
        payload.day = formData.day;
      if (formData.start !== (editAvailabilityData.start || ""))
        payload.start = formData.start;
      if (formData.end !== (editAvailabilityData.end || ""))
        payload.end = formData.end;
    }

    apiCall({
      type:
        mode === "add"
          ? `${CREATE_SUPPLIER}staff/${staffId}/availability/`
          : `${CREATE_SUPPLIER}staff/${staffId}/availability/${editAvailabilityData?.id}/`,
      payload,
      apiType: mode === "add" ? "POST" : "PATCH",
    })
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch((error: any) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setLoadings((prev) => ({ ...prev, handleFormSubmission: false }));
      });
  };

  const handleDeleteAvailability = () => {
    setLoadings((prev) => ({ ...prev, handleDeleteAvailability: true }));

    apiCall({
      type: `${CREATE_SUPPLIER}staff/${staffId}/availability/${editAvailabilityData?.id}/`,
      apiType: "DELETE",
    })
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch((error: any) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setLoadings((prev) => ({ ...prev, handleDeleteAvailability: false }));
      });
  };

  // Effects
  useEffect(() => {
    getWeekDaysList();

    if (mode === "edit" && editAvailabilityData) {
      setFormData({
        start: editAvailabilityData?.start || "",
        end: editAvailabilityData?.end || "",
        day: editAvailabilityData?.day || "",
      });
    }
  }, [getWeekDaysList, mode, editAvailabilityData]);

  return (
    <Modal
      isOpen={visibility}
      onClose={onClose}
      modalHeading={
        mode === "add"
          ? t("add_new_availability")
          : t("edit_availability", { day: editAvailabilityData?.day })
      }
      children={
        <div className="ManageAvailabilityModal">
          <div className="inputs">
            <Dropdown
              label={t("day_of_week") as string}
              dropDownData={weekDaysList}
              setSelectedValue={(value: any) => {
                setFormData((prev) => ({
                  ...prev,
                  day: value,
                }));
              }}
              value={formData.day}
              displayKey={"display"}
              valueKey={"key"}
              selectedValue={formData.day}
            />
            <Dropdown
              label={t("start_time") as string}
              dropDownData={TIME_ARRAY}
              setSelectedValue={(value: any) => {
                setFormData((prev) => ({
                  ...prev,
                  start: value,
                }));
              }}
              value={formData.start}
              displayKey={"displayName"}
              valueKey={"value"}
              selectedValue={formData.start}
            />
            <Dropdown
              label={t("end_time") as string}
              dropDownData={TIME_ARRAY}
              setSelectedValue={(value: any) => {
                setFormData((prev) => ({
                  ...prev,
                  end: value,
                }));
              }}
              value={formData.end}
              displayKey={"displayName"}
              valueKey={"value"}
              selectedValue={formData.end}
            />
          </div>
          <div className="buttons">
            <Button
              title={mode === "add" ? t("add_availability") : t("save_changes")}
              variant={"primary"}
              onClick={handleFormSubmission}
              isLoading={loadings.handleFormSubmission}
              disabled={
                loadings.handleFormSubmission ||
                loadings?.handleDeleteAvailability ||
                !canSubmitForm
              }
            />
            {mode === "edit" ? (
              <Button
                title={t("delete_availability")}
                variant={"secondary"}
                onClick={handleDeleteAvailability}
                isLoading={loadings.handleDeleteAvailability}
                disabled={
                  loadings.handleDeleteAvailability ||
                  loadings?.handleFormSubmission
                }
              />
            ) : null}
          </div>
        </div>
      }
    />
  );
};

export default ManageAvailabilityModal;
